import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  QueryList,
  ViewChildren,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Titles } from "src/app/core/resources/titles";
import { SnackBarService } from "src/app/core/services/snackBar.service";
import { Utils } from "src/app/core/resources/utils";
import { FormControl, Validators } from "@angular/forms";
import { FileComponent } from "../files/file.component";
import { DateManager } from "src/app/core/managers/date.manager";
import { FormMessages } from "src/app/core/messages/form-messages.enum";
import { Fmt } from "src/app/core/messages/fmt";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { DatePipe } from "@angular/common";
import { Router } from "@angular/router";
import { OptionsDialog } from "src/app/core/interfaces/options-dialog";
import {
  CargoPaymentPreviewResponse,
  CargoPaymentPreviewSwitches
} from "src/app/core/interfaces/bank-account-payment-detail";

/**
 * Switches que alteran el monto del pago y por tanto obligan a recalcular el
 * preview cuando el usuario los mueve.
 */
const PAYMENT_PREVIEW_SWITCH_IDS = ["payWithAdditionalCost", "payWithTravelExpenses"];
@Component({
  selector: "app-dialog",
  templateUrl: "./dialog.component.html",
  styleUrls: ["./dialog.component.scss"],
  providers: [DatePipe]
})
export class DialogComponent implements OnInit, OnDestroy, AfterViewInit {
  myFilter = DateManager.filter({ from: this.data.fromDate, until: this.data.untilDate });
  @ViewChildren(FileComponent) fileComponent: QueryList<FileComponent>;
  @ViewChild('htmlContainer', { static: true }) htmlContainer!: ElementRef;
  textAreaMessage: string = "";
  date: FormControl = new FormControl('', Validators.required);
  dateAndTime: FormControl = new FormControl('', Validators.required);
  inputDocumentUser: FormControl = new FormControl('', [Validators.required]);
  dateFormatted: string = "";
  inputText: string = "";
  inputDescription: string = "";
  selectedOption: string = "";
  inputNumber: number = 0;
  itemList: any = {};
  inputExtraAdvance: FormControl;
  refuse: string = "";
  checkbox = [];
  invalidDate: boolean = false;
  minDate: Date = new Date();
  resourceEvent: Event;
  inputCurrency: number = null;
  paymentPreview: CargoPaymentPreviewResponse;
  paymentPreviewLoading: boolean = false;
  paymentPreviewRequested: boolean = false;
  paymentPreviewError: string;
  private destroyed = false;
  constructor(
    public dialogRef: MatDialogRef<DialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: OptionsDialog,
    public titles: Titles,
    private snackBarService: SnackBarService,
    public utils: Utils,
    private dateManager: DateManager,
    private sanitizer: DomSanitizer,
    public datePipe: DatePipe,
    private router: Router,
  ) {
    if (this.data && this.data.descriptionHTML)
      this.data.descriptionHTML = this.sanitizer.bypassSecurityTrustHtml(this.data.descriptionHTML as string);
    if (this.data && data.inputText) {
      if (!this.utils.isDefined(data.maxLength)) this.data.maxLength = 20;
      if (!this.utils.isDefined(data.minLength)) this.data.minLength = 4;
    }
    if (this.data && data.inputDescription && !this.utils.isDefined(data.maxLengthDescription))
      this.data.maxLengthDescription = 150;
    if (this.data && this.data.dateAndTime && this.data.initialDateAndTime)
      this.dateAndTime.setValue(DateManager.dateToString(this.data.initialDateAndTime, 'YYYY-MM-DDTHH:mm'));
  }

  ngOnInit() {
    const validators = [Validators.required, Validators.min(100)];
    if (this.data && this.data.maxAdvance) {
      validators.push(Validators.max(Number(this.data.maxAdvance)));
    }
    this.inputExtraAdvance = new FormControl('', validators);
  }

  ngOnDestroy() {
    this.destroyed = true;
  }

  get showInputText(): boolean {
    if (!this.data || !this.data.inputText) return false;
    return !this.data.inputTextShowOnOption || this.data.inputTextShowOnOption === this.selectedOption;
  }

  get showInputDescription(): boolean {
    if (!this.data || !this.data.inputDescription) return false;
    return !this.data.inputDescriptionShowOnOption || this.data.inputDescriptionShowOnOption === this.selectedOption;
  }

  /**
   * Consulta el preview del pago. Puede llamarse varias veces: cada vez que el
   * usuario mueve un switch los montos cambian y hay que recalcularlo.
   * Un fallo no bloquea el pago, solo se informa que no pudo verificarse.
   */
  async loadPaymentPreview(): Promise<void> {
    if (!this.data.loadPaymentPreview || this.paymentPreviewLoading) return;

    this.paymentPreviewLoading = true;
    this.paymentPreviewRequested = true;
    this.paymentPreviewError = null;
    try {
      const preview = await this.data.loadPaymentPreview(this.paymentPreviewSwitches);
      // El usuario pudo cerrar el modal mientras se consultaba: tocar el
      // overlay ya destruido rompe.
      if (this.destroyed) return;
      this.paymentPreview = preview;
      if (this.data.paymentPreviewDialogWidth)
        this.dialogRef.updateSize(this.data.paymentPreviewDialogWidth);
    } catch (error) {
      if (this.destroyed) return;
      this.paymentPreview = null;
      this.paymentPreviewError =
        "No fue posible consultar el detalle del pago. Puedes continuar, pero no se pudieron verificar las cuentas bancarias ni los valores.";
    } finally {
      this.paymentPreviewLoading = false;
    }
  }

  get paymentPreviewSwitches(): CargoPaymentPreviewSwitches {
    return {
      payWithAdditionalCost: this.isCheckboxChecked("payWithAdditionalCost"),
      payWithTravelExpenses: this.isCheckboxChecked("payWithTravelExpenses"),
    };
  }

  private isCheckboxChecked(id: string): boolean {
    return this.checkbox.some((item) => item.id === id);
  }

  confirmDialog(refuse?: string) {
    this.refuse = refuse;
    this.closeDialog(true);
  }

  closeDialog(response?: boolean) {
    let dataResponse: any = {
      state: response,
      refuse: this.refuse,
    };
    if (response) {
      // textArea
      if (this.data.textArea && this.textAreaMessage.trim().length < 1) {
        this.snackBarService.openSnackBar(
          "Faltan datos por agregar",
          undefined,
          "alert"
        );
        return;
      } else if (this.data.textArea) {
        dataResponse.message = this.textAreaMessage.trim();
      }
      // date
      if (this.data.date) {
        this.date.markAsTouched();
        this.invalidDate = !DateManager.isValidDate(this.dateFormatted);
        if (this.invalidDate)
          return this.snackBarService.openSnackBar("No se ha seleccionado una fecha válida", undefined, "alert");
        dataResponse.date = this.dateFormatted;
      }
      if (this.data.dateAndTime) {
        this.dateAndTime.markAsTouched();
        if (!this.dateAndTime.value)
          return this.snackBarService.openSnackBar("No se ha seleccionado una fecha y hora válida", undefined, "alert");
        const dateAndTime = DateManager.stringToDate(this.dateAndTime.value, 'YYYY-MM-DDTHH:mm');
        this.invalidDate = !DateManager.isValidDate(dateAndTime);
        if (this.invalidDate)
          return this.snackBarService.openSnackBar("No se ha seleccionado una fecha y hora válida", undefined, "alert");
        if (this.data.fromDate && DateManager.isBefore(dateAndTime, this.data.fromDate, 'minutes'))
          return this.snackBarService.openSnackBar(`La fecha y hora no puede ser anterior a ${DateManager.dateToString(this.data.fromDate, 'DD/MM/YYYY HH:mm a')}`, undefined, "alert");
        if (this.data.untilDate && DateManager.isBefore(this.data.untilDate, dateAndTime, 'minutes'))
          return this.snackBarService.openSnackBar(`La fecha y hora no puede ser posterior a ${DateManager.dateToString(this.data.untilDate, 'DD/MM/YYYY HH:mm a')}`, undefined, "alert");
        dataResponse.dateAndTime = dateAndTime;
      }
      // inputDocumentUser
      if (this.data.inputDocumentUser) {
        if (this.inputDocumentUser.invalid || !this.inputDocumentUser.value || !this.inputDocumentUser.value.information || !this.inputDocumentUser.value.information.document)
          return this.snackBarService.openSnackBar("Por favor seleccione un usuario existente", undefined, "alert");
        dataResponse.inputDocumentUser = this.inputDocumentUser.value;
      }
      // select
      if (this.data.viewSelect && this.utils.objIsEmpty(this.itemList)) {
        this.snackBarService.openSnackBar(
          "No ha seleccionado ninguna opción",
          undefined,
          "alert"
        );
        return;
      } else if (this.data.viewSelect && this.utils.isDefined(this.itemList)) {
        dataResponse.itemSelected = this.itemList;
      }

      if (this.data.inputNumber && this.inputNumber < 1) {
        this.snackBarService.openSnackBar(
          "Ingresa un valor para continuar",
          undefined,
          "alert"
        );
        return;
      } else if (this.inputNumber) {
        dataResponse.inputNumber = this.inputNumber;
      }
      // selector
      if (this.data.selectorOptions && this.data.selectorOptions.length) {
        if (!this.selectedOption)
          return this.snackBarService.openSnackBar(`Debes seleccionar una opción en "${this.data.selectorLabel}"`, undefined, "alert");
        dataResponse.selectedOption = this.selectedOption;
      }

      if (this.showInputText) {
        if (this.inputText.trim().length < this.data.minLength)
          return this.snackBarService.openSnackBar(`El campo ${this.data.placeholder.toLocaleLowerCase()} debe tener al menos ${this.data.minLength} caracteres`, undefined, "alert");
        if (this.inputText.length > this.data.maxLength)
          return this.snackBarService.openSnackBar(`El campo ${this.data.placeholder.toLocaleLowerCase()} no puede tener más de ${this.data.maxLength} caracteres`, undefined, "alert");
        dataResponse.inputText = this.inputText.trim();
      }

      if (this.showInputDescription) {
        const description = this.inputDescription.trim();
        if (!description.length)
          return this.snackBarService.openSnackBar(`El campo ${this.data.placeholderDescription.toLocaleLowerCase()} es obligatorio`, undefined, "alert");
        dataResponse.inputDescription = description;
      }

      if (this.data.inputCurrency && this.inputCurrency < 1) {
        this.snackBarService.openSnackBar(
          `El valor del ${this.data.placeholderInputCurrency} debe ser superior a 0`,
          undefined,
          "alert"
        );
        return;
      } else dataResponse.inputCurrency = this.inputCurrency;
      if (this.data.inputExtraAdvance) {
        const extraAdvanceValue = Number(this.inputExtraAdvance.value);
        if (extraAdvanceValue < 1) {
          this.snackBarService.openSnackBar("Ingresa un valor para continuar", undefined, "error");
          return;
        }
        if (extraAdvanceValue > Number(this.data.maxAdvance)) {
          this.snackBarService.openSnackBar("El valor no puede superar el saldo disponible", undefined, "error");
          return;
        } else {
          dataResponse.inputExtraAdvance = extraAdvanceValue;
        }
      }

      //
      if (this.data.file && this.data.inputFormControl.invalid) {
        this.snackBarService.openSnackBar(
          "Debes cargar un archivo",
          undefined,
          "alert"
        );
        return;
      } else if (
        this.data.inputFormControl &&
        this.data.inputFormControl.valid
      ) {
        if (this.data && this.data.fileOptions && !this.data.fileOptions.onlyProcessPaths) this.loadDocument();
        dataResponse.filePath = this.data.inputFormControl.value;
        dataResponse.resourceEvent = this.resourceEvent;
      }

      if (this.data.checkbox && this.checkbox.length > 0) {
        dataResponse.checkbox = this.checkbox;
      }
    }
    if (this.data.btnDetailCargo && !this.data.isTracking) {
      this.router.navigate([this.data.path]);
    }
    this.dialogRef.close(dataResponse);
  }

  ngAfterViewInit() { }

  loadDocument() {
    this.fileComponent.toArray().map((component) => {
      component.loadFileToStorage();
    });
  }

  onChangeDate($event: MatDatepickerInputEvent<Date>) {
    this.invalidDate = false;
    this.dateFormatted = DateManager.dateToString($event.value, 'YYYY-MM-DD HH:mm ZZ');
  }

  onCheckboxChange(checked: boolean, elemento: string, id: string) {
    const container = document.getElementById('descriptionHTML');
    if (container) {
      const showExtraRows = document.querySelectorAll('.show-extra-content');
      showExtraRows.forEach((row) => {
        const element = row as HTMLElement;
        element.style.display = checked
          ? 'table-cell'
          : 'none';
      });

      const hideContents = document.querySelectorAll('.hide-content');
      hideContents.forEach((content) => {
        const element = content as HTMLElement;
        element.style.display = checked
          ? 'none'
          : 'table-cell';
      });
    }
    if (checked) {
      this.checkbox.push({
        name: elemento,
        id: id,
      });
    } else {
      const index = this.checkbox.findIndex(
        (obj) => obj.name === elemento && obj.id === id
      );
      if (index >= 0) {
        this.checkbox.splice(index, 1);
      }
    }

    // Los switches de pago cambian los montos: si el preview ya está en
    // pantalla hay que recalcularlo para no mostrar valores desactualizados.
    if (this.paymentPreviewRequested && PAYMENT_PREVIEW_SWITCH_IDS.indexOf(id) >= 0) {
      this.loadPaymentPreview();
    }
  }
}
